import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useBrandTheme } from "@/lib/brand";
import { MessageSquareText, ArrowLeft, Check, ShieldCheck, MessageCircle, Mail, Phone as PhoneIcon } from "lucide-react";
import { toast } from "sonner";
import { BrandFont } from "@/components/BrandFont";
import { api } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Optin() {
  const { slug } = useParams();
  const [community, setCommunity] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", community: "", unit: "" });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.get(`/optin-community/${slug}`)
      .then((r) => { setCommunity(r.data); setNotFound(false); setForm((f) => ({ ...f, community: r.data.name })); })
      // iter252/L9, an unknown slug used to silently render the generic Redline page, so a
      // resident with a stale/typo'd QR code saw a form that couldn't be tied to their
      // community. Say so instead.
      .catch(() => { setCommunity(null); setNotFound(true); });
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please check the consent box to opt in."); return; }
    if (!form.phone) { toast.error("Please enter your mobile number."); return; }
    setSending(true);
    try {
      await api.post("/opt-in", { ...form, tenant_id: community?.id || "", consent });
      setDone(true);
      toast.success("You're opted in. Reply STOP anytime to unsubscribe.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  // Community-first look when we have one; otherwise the platform-default Redline look.
  const communityName = community?.name || "";
  const brand = (community?.brand_color || "").trim() || "#D32F2F";
  useBrandTheme(community?.brand_color);
  const brandTintBg = brand === "#D32F2F" ? "#FFF1F1" : `color-mix(in srgb, ${brand} 10%, white)`;
  const logoUrl = community?.has_logo
    ? `${API}/public/site/${community?.public_site_slug || slug}/logo?v=${community?.logo_ver || ""}`
    : "";
  const heading = communityName
    ? `Get community texts from ${communityName}`
    : "Get community texts from Ask Redline";
  const mgmtDisplay = community?.mgmt_name || "";
  const contactEmail = community?.contact_email || "";
  const contactPhone = community?.contact_phone || "";
  const conciergeNum = community?.concierge_number || "";
  const voice = !!community?.voice_ai_enabled;

  if (notFound) {
    return (
      <div data-testid="optin-not-found" className="min-h-screen bg-sand-50 font-body text-ink flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-white border border-sand-200 flex items-center justify-center">
            <MessageSquareText className="h-6 w-6 text-ink/40" strokeWidth={1.75} />
          </div>
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl mt-4">We couldn&apos;t find that community</h1>
          <p className="text-ink/60 mt-2">
            This sign-up link looks out of date. Check the QR code or link your community sent you, or ask your
            community office for a fresh one.
          </p>
          <Link to="/" className="inline-block mt-6 rounded-full bg-ink text-white px-5 py-2.5 text-sm font-semibold hover:bg-ink/90 transition-colors">
            Go to Ask Redline
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50 font-body text-ink" data-brandfont data-testid="optin-page" style={{ "--brand": brand }}>
      <BrandFont brand={community} />
      <nav className="border-b border-sand-200 bg-sand-50/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Community branding takes the header when we have it; Redline stays out of the way. */}
          {community ? (
            <div className="flex items-center gap-2.5 min-w-0" data-testid="optin-community-brand">
              {logoUrl ? (
                <img src={logoUrl} alt={`${communityName} logo`} className="h-9 max-w-[140px] object-contain" />
              ) : (
                <div className="h-9 w-9 rounded-xl flex items-center justify-center text-white font-heading font-black text-sm" style={{ backgroundColor: brand }}>
                  {(communityName || "C").slice(0, 1).toUpperCase()}
                </div>
              )}
              <span className="font-heading font-extrabold tracking-tight truncate">{communityName}</span>
            </div>
          ) : (
            <Link to="/" className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-xl bg-redline-500 flex items-center justify-center">
                <MessageSquareText className="h-5 w-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-heading font-extrabold tracking-tight">Ask Redline</span>
            </Link>
          )}
          {!community && (
            <Link to="/" data-testid="optin-back" className="flex items-center gap-1.5 text-sm font-semibold text-redline-600 hover:text-redline-700 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Back to site
            </Link>
          )}
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-6 py-14">
        <p className="text-xs uppercase tracking-[0.2em] font-bold mb-3" style={{ color: brand }}>SMS opt-in</p>
        <h1 data-testid="optin-heading" className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">{heading}</h1>
        <p className="mt-4 text-ink-700 leading-relaxed">
          {communityName
            ? `${communityName} uses ${voice ? "an AI concierge you can call or text" : "a text-message concierge"} for your community.`
            : `Ask Redline is the ${voice ? "AI concierge you can call or text" : "text-message concierge"} for your community.`}{" "}
          Opt in below to {voice ? "call or text" : "text"} one dedicated
          number and get instant answers from your community&apos;s documents, log maintenance requests, RSVP to events,
          and receive important safety and operations alerts.
        </p>

        {/* What you'll receive */}
        <div className="mt-8 rounded-2xl border border-sand-200 bg-white p-6">
          <h2 className="font-heading font-bold text-lg mb-4">What you&apos;ll receive</h2>
          <ul className="space-y-2.5 text-sm text-ink-800">
            {(community?.receive_bullets?.length ? community.receive_bullets : [
              `Answers to questions about ${communityName || "your community"}\u2019s rules, amenities and policies`,
              "Confirmations and updates on maintenance requests and RSVPs you submit",
              "Community operations and safety alerts (e.g., \u201cgate down, use the east entrance\u201d)",
            ]).map((t) => (
              <li key={t} className="flex items-start gap-2.5">
                <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: brand }} strokeWidth={2.5} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {done ? (
          <div data-testid="optin-success" className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500 flex items-center justify-center mb-4">
              <Check className="h-6 w-6 text-white" strokeWidth={2.5} />
            </div>
            <h2 className="font-heading font-extrabold text-xl">You&apos;re opted in</h2>
            <p className="text-ink-700 text-sm mt-2 max-w-md mx-auto">
              Thanks! You&apos;ll start receiving texts from {communityName || "your community"}. Reply <b>STOP</b> at any time to unsubscribe,
              or <b>HELP</b> for help.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 rounded-2xl border border-sand-200 bg-white p-6 space-y-4" data-testid="optin-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5">Full name <span className="text-ink-400 font-normal">(optional)</span></label>
                <input data-testid="optin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  style={{ "--tw-ring-color": `${brand}66` }}
                  className="w-full rounded-xl border border-sand-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent" placeholder="Jane Resident" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1.5">Community {!community && <span className="text-ink-400 font-normal">(optional)</span>}</label>
                <input data-testid="optin-community" value={form.community} readOnly={!!community}
                  onChange={(e) => setForm({ ...form, community: e.target.value })}
                  style={{ "--tw-ring-color": `${brand}66` }}
                  className={`w-full rounded-xl border border-sand-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent ${community ? "bg-sand-50 text-ink-600 cursor-default" : ""}`} placeholder="Maplewood HOA" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">Mobile number <span style={{ color: brand }}>*</span></label>
              <input data-testid="optin-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                style={{ "--tw-ring-color": `${brand}66` }}
                className="w-full rounded-xl border border-sand-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent" placeholder="(512) 555-0142" required />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1.5">Your unit or home <span className="text-ink-400 font-normal">(helps {communityName || "your community"} add you to the right home)</span></label>
              <input data-testid="optin-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                style={{ "--tw-ring-color": `${brand}66` }}
                className="w-full rounded-xl border border-sand-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent" placeholder="e.g. Unit 12 or 123 Oak St" />
            </div>

            {/* Consent checkbox, TCPA-required disclosure of the platform operator (Redline
                Monitoring Services Inc.) stays in the fine print regardless of visual brand. */}
            <label className="flex items-start gap-3 rounded-xl border border-sand-200 p-4 cursor-pointer" style={{ backgroundColor: brandTintBg }}>
              <input data-testid="optin-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 rounded border-sand-300" style={{ accentColor: brand }} />
              <span className="text-[13px] leading-relaxed text-ink-700">
                I agree to receive recurring automated text messages{communityName ? ` for ${communityName}` : ""} from
                Ask Redline (operated by Redline Monitoring Services Inc. (doing business as Ask Redline)) at the mobile number I provided, including
                community updates, answers to my questions, maintenance/request notifications, and safety alerts.{" "}
                <b>Message frequency varies. Message and data rates may apply.</b> Reply <b>STOP</b> to opt out or{" "}
                <b>HELP</b> for help. Consent is not a condition of any purchase. See our{" "}
                <Link to="/privacy" className="underline hover:opacity-80" style={{ color: brand }}>Privacy Policy</Link> and{" "}
                <Link to="/terms" className="underline hover:opacity-80" style={{ color: brand }}>Terms of Service</Link>.
              </span>
            </label>

            <button data-testid="optin-submit" type="submit" disabled={sending}
              style={{ backgroundColor: brand }}
              className="w-full rounded-full text-white font-semibold px-6 py-3 hover:brightness-110 transition-[filter] disabled:opacity-60">
              {sending ? "Submitting…" : `Opt in to ${communityName ? communityName : "community"} texts`}
            </button>
            <p className="text-xs text-ink-400 text-center">
              We never sell or share your mobile number with third parties for marketing.
            </p>
          </form>
        )}

        {/* Alternate opt-in method */}
        <div className="mt-8 rounded-2xl border border-sand-200 bg-white p-6">
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle className="h-5 w-5" style={{ color: brand }} strokeWidth={1.75} />
            <h2 className="font-heading font-bold text-lg">Prefer to text?</h2>
          </div>
          <p className="text-sm text-ink-700 leading-relaxed">
            You can also opt in {conciergeNum ? <>by texting <a href={`sms:${conciergeNum}`} className="font-semibold underline" style={{ color: brand }}>{conciergeNum}</a></> : "by texting your community\u2019s dedicated number (shared by your HOA or on community signage)"}. You&apos;ll get a reply confirming enrollment and how to get help. Message frequency varies.
            Message and data rates may apply. Reply <b>STOP</b> to opt out or <b>HELP</b> for help at any time.
          </p>
        </div>

        {/* Community contact, only when the manager filled in their details */}
        {(mgmtDisplay || contactEmail || contactPhone) && (
          <div className="mt-6 rounded-2xl border border-sand-200 bg-white p-6" data-testid="optin-mgmt-block">
            <h2 className="font-heading font-bold text-lg mb-2">Managed by {mgmtDisplay || communityName}</h2>
            <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-ink-700">
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} className="flex items-center gap-1.5 hover:opacity-80" style={{ color: brand }}>
                  <Mail className="h-4 w-4" /> {contactEmail}
                </a>
              )}
              {contactPhone && (
                <a href={`tel:${contactPhone}`} className="flex items-center gap-1.5 hover:opacity-80" style={{ color: brand }}>
                  <PhoneIcon className="h-4 w-4" /> {contactPhone}
                </a>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 text-xs text-ink-400">
          <ShieldCheck className="h-4 w-4" />
          Consent records are stored securely and used only to operate the messaging service.
        </div>
      </main>

      <footer className="border-t border-sand-200 py-8 text-center text-xs text-muted-foreground px-6">
        {community ? (
          <>SMS service operated by Redline Monitoring Services Inc. (doing business as Ask Redline) on behalf of {communityName}. Consent records kept confidential, never sold or shared for marketing.</>
        ) : (
          <>© {new Date().getFullYear()} Ask Redline · Austin, TX</>
        )}
      </footer>
    </div>
  );
}
